import { forwardRef } from 'react';
import { InputVariant } from './Input';

interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label?: string;
  error?: string;
  variant?: InputVariant;
  fullWidth?: boolean;
  loading?: boolean;
}

const Skeleton = ({ className }: { className: string }) => (
  <div className={`animate-pulse bg-primary-100 dark:bg-primary-800 rounded ${className}`} />
);

const variantStyles = {
  primary: {
    label: 'block text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-2',
    textarea: 'w-full px-4 py-3 border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 text-primary-950 dark:text-white focus:outline-none focus:border-primary-400 transition-colors duration-300',
    error: 'mt-1 text-sm text-red-600 dark:text-red-400',
  },
  secondary: {
    label: 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2',
    textarea: 'appearance-none relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 transition duration-150',
    error: 'mt-1 text-sm text-red-600 dark:text-red-400',
  },
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, variant = 'primary', fullWidth = true, loading = false, id, ...props }, ref) => {
    const styles = variantStyles[variant];
    const textareaId = id || `textarea-${Math.random().toString(36).substring(2, 11)}`;

    if (loading) {
      return (
        <div>
          {label && (
            <Skeleton className={`${variant === 'primary' ? 'h-4 w-24 mb-2' : 'h-5 w-32 mb-2'}`} />
          )}
          <Skeleton className="h-24 w-full" />
          {error && (
            <Skeleton className="h-4 w-48 mt-1" />
          )}
        </div>
      );
    }

    return (
      <div>
        {label && (
          <label
            htmlFor={textareaId}
            className={styles.label}
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={`${styles.textarea} ${fullWidth ? 'w-full' : ''}`}
          {...props}
        />
        {error && (
          <p className={styles.error}>{error}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';